import { makeAutoObservable, runInAction } from 'mobx'
import { $RootStore } from './RootStore'
import analytics from './analytics'
import { splitPhoneNumber, validatePhoneNumber } from '../../lib/utils'

export type UploadDocument = {
  url: string
  status: string
}

export type Qualification = {
  title: string
  dateObtained: Date
  files?: UploadDocument[]
}

export interface Document {
  title: string
  dateAdded: Date | null
  expiryDate: Date | null
  status: string | null
  files?: UploadDocument[]
}

export type Application = {
  firstName: string
  lastName: string
  emailAddress: string
  gender: string
  postalCode: string
  city: string
  mobile: string
  referral: string
  referralName: string
  categories: string[]
  qualifications: Qualification[]
  old_qualifications?: string
  status: string
  documents: Document[]
  old_documents?: string[]
  interviewDate: Date | null
  bookingsPerWeek: string
  currentSituation: string
  currentSituationOther?: string
}

export const defaultApplicationValues: Application = {
  firstName: '',
  lastName: '',
  emailAddress: '',
  gender: '',
  postalCode: '',
  city: '',
  mobile: '+44',
  referral: '',
  categories: [],
  qualifications: [],
  status: 'in-progress',
  referralName: '',
  currentSituation: '',
  bookingsPerWeek: '',
  documents: [
    {
      title: 'Insurance',
      dateAdded: null,
      expiryDate: null,
      status: null,
      files: [],
    },
    {
      title: 'Photo ID',
      dateAdded: null,
      expiryDate: null,
      status: null,
      files: [],
    },
    {
      title: 'DBS Certificate',
      dateAdded: null,
      expiryDate: null,
      status: null,
      files: [],
    },
  ],
  interviewDate: null,
  currentSituationOther: undefined,
}

export type $ApplicationStore = ApplicationStore

export default class ApplicationStore {
  store: $RootStore
  applicationId: undefined | string = undefined
  data: Application = { ...defaultApplicationValues }
  editingQualificationIndex: undefined | number = undefined
  editingDocumentIndex: undefined | number = undefined

  constructor(store: $RootStore) {
    this.store = store

    makeAutoObservable(this)
  }

  onPageLoad = async () => {
    if (!this.applicationId) {
      const id = localStorage.getItem('id')
      if (!id) {
        this.clearApplicationData()
        return
      }

      await this.store.populateFromStoreAndRedirect()
    }
  }

  formatIdString = (id: string) => {
    return id.replaceAll('APPLICATION_', '').replaceAll('APPLICANT_', '')
  }

  loadApplication = async (_id: string, emailAddress: string) => {
    const { communication, errorHandler } = this.store

    if (!communication) return

    if (!!_id && !!emailAddress) {
      const { professionalApplication } = await communication.requester.professionalApplication({ _id, emailAddress })

      if (!professionalApplication) {
        return false
      }

      runInAction(() => {
        this.data = { ...professionalApplication } as Application
        this.applicationId = professionalApplication._id as string
        this.identifyUser()
      })

      errorHandler.setUserContext(professionalApplication)

      this.syncToStorage()
      return professionalApplication
    }

    return false
  }

  createApplication = async () => {
    const { communication: { requester }, router } = this.store
    const numberParts = splitPhoneNumber(this.data.mobile)

    let addProfessionalApplication

    try {
      const response = await requester.addProfessionalApplication({
        data: {
          ...this.data,
          mobile: validatePhoneNumber(numberParts[1], numberParts[0]),
        },
      })

      addProfessionalApplication = response.addProfessionalApplication

    } catch (error: any) {
      if (error.message === 'Existing application for this mobile or email') {
        const foundApplication = await this.fetchExistingApplication()

        if (foundApplication) {
          analytics.track('Fetched Existing Application', { ...this.data })
          router.push('/apply/submit')
          return
        }
      }
    }

    if (!addProfessionalApplication) return

    this.applicationId = addProfessionalApplication._id as string

    this.syncToStorage()

    return addProfessionalApplication
  }

  updateApplication = async () => {
    const { communication: { requester }, uiStore: { showToast } } = this.store

    if (!this.applicationId) return

    const numberParts = splitPhoneNumber(this.data.mobile)

    const { updateProfessionalApplication } = await requester.updateProfessionalApplication({
      _id: this.applicationId,
      data: {
        ...this.data,
        mobile: validatePhoneNumber(numberParts[1], numberParts[0]),
      },
    })

    if (!updateProfessionalApplication) {
      showToast('Failed to update your application.', 'error')
      return false
    }

    this.data = { ...updateProfessionalApplication } as Application

    this.syncToStorage()

    return true
  }

  clearApplicationData = (route = '/apply') => {
    const { router } = this.store

    localStorage.clear()
    this.data = { ...defaultApplicationValues }
    router.push(route)
  }

  editQualification = (index: number) => {
    const { router } = this.store
    this.editingQualificationIndex = index

    router.push('qualifications/edit')
  }

  editDocument = (index: number) => {
    const { router } = this.store

    this.setEditingDocumentIndex(index)

    router.push('documents/add')
  }

  setEditingDocumentIndex = (index: number | undefined) => {
    this.editingDocumentIndex = index
  }

  fetchExistingApplication = async () => {
    const { communication: { requester } } = this.store
    const mobileParts = splitPhoneNumber(this.data.mobile)
    const { fetchExistingApplication } = await requester.fetchExistingApplication({
      mobile: validatePhoneNumber(mobileParts[1], mobileParts[0]),
      emailAddress: this.data.emailAddress,
    })

    if (!fetchExistingApplication) return

    this.data.status = fetchExistingApplication.status === 'in-progress' ? 'existing_in_progress' : 'existing_applied'
    this.data.emailAddress = fetchExistingApplication.emailAddress as string
    this.applicationId = fetchExistingApplication._id as string

    this.identifyUser()

    return true
  }

  identifyUser = () => {
    if (!this.applicationId) return

    analytics.identify(`APPLICATION_${this.applicationId}`, {
      firstName: this.data.firstName,
      lastName: this.data.lastName,
      emailAddress: this.data.emailAddress,
      email: this.data.emailAddress,
      mobile: this.data.mobile,
      phone: this.data.mobile,
    })
  }

  syncToStorage = () => {
    this.applicationId && window.localStorage.setItem('id', this.applicationId)
    window.localStorage.setItem('emailAddress', this.data.emailAddress)
  }
}